import PropTypes from 'prop-types';
import React from "react";
import {BuildingLibraryIcon, BuildingOfficeIcon} from "@heroicons/react/24/outline";
import {useTranslation} from "react-i18next";
import {AnimatePresence, motion} from "framer-motion";
import {useSales} from "@/pages/workspaces/sales/SalesContext";
import titleCase from "@/utils/titleCase";

const SITE_STYLES = {
  'network/operational': {
    icon: 'bg-cs-orange-600 text-white',
    details: 'bg-cs-orange-100'
  },
  'network/on_hold': {
    icon: 'text-cs-gray-800',
    details: 'bg-cs-gray-200'
  },
  'single/operational': {
    icon: 'bg-cs-blue-600 text-white',
    details: 'bg-cs-blue-100'
  },
  'single/on_hold': {
    icon: 'bg-cs-gray-800 text-white',
    details: 'bg-cs-gray-200'
  }
}

SiteIcon.propTypes = {
  site: PropTypes.shape({
    networkCount: PropTypes.number,
    isActive: PropTypes.bool,
  }),
  className: PropTypes.string,
}
function SiteIcon({site, className}) {
  if (site.networkCount > 1)
    return <BuildingOfficeIcon className={className}/>
  return <BuildingLibraryIcon className={className}/>
}

export default function SiteDetails() {
  const {state} = useSales();
  const {clickedItem} = state;
  const siteId = clickedItem?.properties?.id;
  const {t} = useTranslation();

  const siteKind = clickedItem?.properties?.networkCount > 1 ? 'network' : 'single'
  const siteKindKey = siteKind === 'network' ? 'workspace.sales.network_site' : 'workspace.sales.single_site'
  const siteFunding = clickedItem?.properties?.isActive ? 'operational' : 'on_hold'
  const siteFundingKey = siteFunding === 'operational' ? 'workspace.sales.siteMap.operational' : 'workspace.sales.siteMap.on_hold'
  const {icon: iconClasses, details: detailClasses} = SITE_STYLES[`${siteKind}/${siteFunding}`]

  if (!clickedItem || clickedItem.cluster) return (
    <p className="mt-1 text-base-content/70 text-base">
      {t('workspace.sales.siteSidebar.no_details')}
    </p>
  );

  return (
    <AnimatePresence>
      <motion.section
        key={`site-details-${siteId}`}
        initial={{opacity: 0, y: 10}}
        animate={{opacity: 1, y: 0}}
        exit={{opacity: 0, y: -10}}
      >
        <h2 className="text-lg font-semibold text-base-content">{t('workspace.sales.siteSidebar.site_details')}:</h2>
        <div className={`p-3 rounded ${detailClasses}`}>
          <div>
            <SiteIcon site={clickedItem.properties}
                      className={`shrink-0 w-6 h-6 inline-block align-top mr-1 ${iconClasses} rounded-full p-0.5`}/>
            <span className="font-semibold text-base-content">{clickedItem.properties.name}</span>
          </div>
          <div className="text-sm text-base-content/70">{titleCase(clickedItem.properties.addressLine1)}
            <span> {titleCase(clickedItem.properties.city)},</span>
            <span> {clickedItem.properties.state}</span>
            <p className="text-xs">{t(siteKindKey)}</p>
            <p className="text-xs">{t(siteFundingKey)}</p>
          </div>
        </div>
      </motion.section>
    </AnimatePresence>
  );
}
